import { useCallback, useState, useEffect } from "react";
import axios from "axios";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Clock,
  Download,
  Loader2,
  PauseCircle,
  Pencil,
  Pill,
  PlayCircle,
  Plus,
  Printer,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import TerapiaExtraDialog from "./TerapiaExtraDialog";
import CambioTerapiaDialog from "./CambioTerapiaDialog";
import { sortTerapieByOrarioNome, summarizeTerapiaExtra } from "../../utils/terapieExtraFormat";
import { stampaTerapieAttive } from "../../utils/terapieInCorsoPrint";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function TodaySlotRow({ slot, onSomministra, submitting, note, onNoteChange }) {
  const isOverdue = slot.stato === "overdue";
  const isDue = slot.stato === "due";
  const isSomministrata = !!slot.somministrata;
  const rowClass = isSomministrata
    ? "bg-emerald-50/70"
    : isOverdue
      ? "bg-red-50/80"
      : isDue
        ? "bg-amber-50/80"
        : "bg-white";

  const badgeClass = isSomministrata
    ? "bg-emerald-600"
    : isOverdue
      ? "bg-red-600"
      : isDue
        ? "bg-amber-500 text-white"
        : "bg-slate-500";

  const badgeLabel = isSomministrata
    ? "Somministrata"
    : isOverdue
      ? "In ritardo"
      : isDue
        ? "Da somministrare"
        : "Programmata";

  return (
    <tr className={`${rowClass} align-top transition-colors`}>
      <td className="whitespace-nowrap px-3 py-3 text-base font-bold tabular-nums text-slate-900">
        {slot.orario || "—"}
      </td>
      <td className="min-w-[220px] px-3 py-3">
        <p className="font-semibold text-slate-900">{slot.nome_terapia}</p>
      </td>
      <td className="min-w-[220px] px-3 py-3">
        {slot.dosaggio ? (
          <p className="text-sm text-slate-700">{slot.dosaggio}</p>
        ) : (
          <span className="text-sm text-slate-400">—</span>
        )}
        {slot.note_terapia ? (
          <p className="text-xs text-slate-500 mt-1 italic">{slot.note_terapia}</p>
        ) : null}
      </td>
      <td className="whitespace-nowrap px-3 py-3">
        <Badge className={badgeClass}>{badgeLabel}</Badge>
        {isSomministrata ? (
          <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" />
            {slot.somministrata_da_nome || "Registrata"}
          </p>
        ) : null}
      </td>
      <td className="min-w-[240px] px-3 py-3">
        {!isSomministrata ? (
          <div className="flex flex-col gap-2">
            <Textarea
              placeholder="Note (opzionale)"
              value={note || ""}
              onChange={(e) => onNoteChange(slot, e.target.value)}
              rows={2}
              className="text-sm bg-white"
            />
            <Button
              size="sm"
              onClick={() => onSomministra(slot)}
              disabled={submitting}
              className={isOverdue ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Check className="w-4 h-4 mr-1" />
              )}
              Segna somministrata
            </Button>
          </div>
        ) : (
          <span className="text-sm text-emerald-700">Completata</span>
        )}
      </td>
    </tr>
  );
}

export default function TerapieInCorsoSection({
  fascicoloId,
  terapieInCorso = [],
  ospiteInfo,
  mode = "admin",
  authHeaders,
  onRefresh,
  onAlertsChange,
  proposteRefreshSignal = 0,
}) {
  const isAdmin = mode === "admin";
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTerapia, setEditingTerapia] = useState(null);
  const [saving, setSaving] = useState(false);
  const [proposte, setProposte] = useState([]);
  const [propostaLoading, setPropostaLoading] = useState(null);
  const [todaySlots, setTodaySlots] = useState([]);
  const [loadingTodaySlots, setLoadingTodaySlots] = useState(true);
  const [submittingId, setSubmittingId] = useState(null);
  const [noteBySlot, setNoteBySlot] = useState({});
  const [cambioDialogOpen, setCambioDialogOpen] = useState(false);
  const [storicoCambi, setStoricoCambi] = useState([]);
  const [loadingStorico, setLoadingStorico] = useState(false);

  const todaySlotsUrl = isAdmin
    ? `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/terapie-in-corso/pending?scope=today`
    : `${API_URL}/api/oss/me/fascicoli/${fascicoloId}/terapie-in-corso/pending?scope=today`;

  const loadTodaySlots = useCallback(() => {
    if (!fascicoloId || !authHeaders) return;
    setLoadingTodaySlots(true);
    axios
      .get(todaySlotsUrl, { headers: authHeaders })
      .then(({ data }) => setTodaySlots(data.items || []))
      .catch(() => setTodaySlots([]))
      .finally(() => setLoadingTodaySlots(false));
  }, [authHeaders, fascicoloId, todaySlotsUrl]);

  useEffect(() => {
    loadTodaySlots();
    const interval = setInterval(loadTodaySlots, 60000);
    return () => clearInterval(interval);
  }, [loadTodaySlots]);

  useEffect(() => {
    if (loadingTodaySlots) return;
    const pending = todaySlots.filter(
      (slot) => !slot.somministrata && ["due", "overdue"].includes(slot.stato)
    );
    onAlertsChange?.(pending.length, {
      overdue: pending.filter((s) => s.stato === "overdue").length,
    });
  }, [todaySlots, loadingTodaySlots, onAlertsChange]);

  const slotKey = (slot) => `${slot.terapia_in_corso_id}::${slot.scheduled_at}`;

  const handleSomministra = async (slot) => {
    if (!fascicoloId || !authHeaders) return;
    const url = isAdmin
      ? `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/terapie-in-corso/${slot.terapia_in_corso_id}/somministra`
      : `${API_URL}/api/oss/me/fascicoli/${fascicoloId}/terapie-in-corso/${slot.terapia_in_corso_id}/somministra`;
    setSubmittingId(slotKey(slot));
    try {
      await axios.post(
        url,
        {
          scheduled_at: slot.scheduled_at,
          note: noteBySlot[slotKey(slot)] || null,
        },
        { headers: authHeaders }
      );
      toast.success("Somministrazione registrata");
      setNoteBySlot((prev) => {
        const next = { ...prev };
        delete next[slotKey(slot)];
        return next;
      });
      loadTodaySlots();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante la registrazione");
    } finally {
      setSubmittingId(null);
    }
  };

  useEffect(() => {
    if (!isAdmin || !authHeaders || !fascicoloId) return;
    axios
      .get(`${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/terapie-proposte`, { headers: authHeaders })
      .then(({ data }) => setProposte(data.items || []))
      .catch(() => setProposte([]));
  }, [fascicoloId, authHeaders, isAdmin, terapieInCorso, proposteRefreshSignal]);

  const loadStoricoCambi = useCallback(() => {
    if (!isAdmin || !authHeaders || !fascicoloId) return;
    setLoadingStorico(true);
    axios
      .get(`${API_URL}/api/deblistering/terapie/ospiti/${fascicoloId}/documenti`, { headers: authHeaders })
      .then(({ data }) => {
        setStoricoCambi((data.items || []).filter((d) => d.tipo === "cambio_terapia"));
      })
      .catch(() => setStoricoCambi([]))
      .finally(() => setLoadingStorico(false));
  }, [authHeaders, fascicoloId, isAdmin]);

  useEffect(() => {
    loadStoricoCambi();
  }, [loadStoricoCambi, proposteRefreshSignal]);

  const handleDownloadCambio = async (doc) => {
    if (!authHeaders || !doc?.id) return;
    try {
      const res = await axios.get(`${API_URL}/api/deblistering/documenti/${doc.id}/download`, {
        headers: authHeaders,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name || "cambio_terapia.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore download documento");
    }
  };

  const handleCambioSuccess = () => {
    loadStoricoCambi();
    onRefresh?.();
    // Ricarica proposte dopo estrazione IA (può richiedere qualche secondo)
    setTimeout(() => {
      if (!isAdmin || !authHeaders || !fascicoloId) return;
      axios
        .get(`${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/terapie-proposte`, { headers: authHeaders })
        .then(({ data }) => setProposte(data.items || []))
        .catch(() => {});
    }, 2500);
  };

  const handleConfermaProposta = async (prop) => {
    setPropostaLoading(prop.id);
    try {
      await axios.post(
        `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/terapie-proposte/${prop.id}/conferma`,
        {},
        { headers: authHeaders }
      );
      toast.success(`Terapia "${prop.farmaco}" confermata`);
      setProposte((prev) => prev.filter((p) => p.id !== prop.id));
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore conferma");
    } finally {
      setPropostaLoading(null);
    }
  };

  const handleRifiutaProposta = async (prop) => {
    setPropostaLoading(prop.id);
    try {
      await axios.delete(
        `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/terapie-proposte/${prop.id}`,
        { headers: authHeaders }
      );
      toast.success("Proposta rifiutata");
      setProposte((prev) => prev.filter((p) => p.id !== prop.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setPropostaLoading(null);
    }
  };

  const handleCreateOrUpdate = async (payload) => {
    if (!isAdmin || !authHeaders) return;
    setSaving(true);
    try {
      if (editingTerapia?.id) {
        await axios.put(
          `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/terapie-in-corso/${editingTerapia.id}`,
          payload,
          { headers: authHeaders }
        );
        toast.success("Terapia aggiornata");
      } else {
        await axios.post(
          `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/terapie-in-corso`,
          payload,
          { headers: authHeaders }
        );
        toast.success("Terapia creata");
      }
      setDialogOpen(false);
      setEditingTerapia(null);
      onRefresh?.();
      loadTodaySlots();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio terapia");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAttiva = async (te) => {
    if (!isAdmin || !authHeaders) return;
    try {
      await axios.put(
        `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/terapie-in-corso/${te.id}`,
        { attiva: !te.attiva },
        { headers: authHeaders }
      );
      toast.success(te.attiva ? "Terapia sospesa" : "Terapia riattivata");
      onRefresh?.();
      loadTodaySlots();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento");
    }
  };

  const handleDelete = async (te) => {
    if (!isAdmin || !authHeaders) return;
    if (!window.confirm(`Eliminare la terapia "${te.nome}"?`)) return;
    try {
      await axios.delete(
        `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/terapie-in-corso/${te.id}`,
        { headers: authHeaders }
      );
      toast.success("Terapia rimossa");
      onRefresh?.();
      loadTodaySlots();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  };

  const sortedTerapie = sortTerapieByOrarioNome(terapieInCorso);

  const terapieAttiveCount = (terapieInCorso || []).filter((te) => te.attiva !== false).length;

  const handleStampaTerapieAttive = () => {
    const result = stampaTerapieAttive(ospiteInfo, terapieInCorso);
    if (result.reason === "no_active") {
      toast.error("Nessuna terapia attiva da stampare");
      return;
    }
    if (result.reason === "popup_blocked") {
      toast.error("Popup bloccato. Abilita i popup per stampare.");
    }
  };

  return (
    <>
      {proposte.length > 0 && isAdmin && (
        <Card className="card-base lg:col-span-2 border-amber-200 bg-amber-50/50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-amber-900">
              <Pill className="w-5 h-5" />
              Proposte terapia da validare ({proposte.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-amber-800">
              Estratte automaticamente da un documento terapia (pre-ingresso o deblistering). Conferma per aggiungerle alle terapie in corso.
            </p>
            {proposte.map((prop) => (
              <div key={prop.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-white rounded-lg border border-amber-200">
                <div>
                  <p className="font-semibold text-slate-900">{prop.farmaco}</p>
                  <p className="text-sm text-slate-600">
                    {[prop.dosaggio, prop.frequenza].filter(Boolean).join(" · ")}
                  </p>
                  {prop.note ? <p className="text-xs text-slate-500 italic mt-1">{prop.note}</p> : null}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={propostaLoading === prop.id}
                    onClick={() => handleRifiutaProposta(prop)}
                  >
                    Rifiuta
                  </Button>
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    disabled={propostaLoading === prop.id}
                    onClick={() => handleConfermaProposta(prop)}
                  >
                    {propostaLoading === prop.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Conferma"
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      <Card className="card-base lg:col-span-2">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Pill className="w-5 h-5 text-blue-500" />
            Terapie in corso
            {todaySlots.filter((slot) => !slot.somministrata && ["due", "overdue"].includes(slot.stato)).length > 0 ? (
              <Badge className="bg-red-500 text-white">
                {todaySlots.filter((slot) => !slot.somministrata && ["due", "overdue"].includes(slot.stato)).length}
              </Badge>
            ) : null}
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleStampaTerapieAttive}
              disabled={terapieAttiveCount === 0}
              data-testid="btn-stampa-terapie-attive"
            >
              <Printer className="w-4 h-4 mr-1" />
              Stampa terapie attive
            </Button>
            {isAdmin ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                  onClick={() => setCambioDialogOpen(true)}
                  data-testid="btn-cambio-terapia"
                >
                  <Upload className="w-4 h-4 mr-1" />
                  Cambio terapia
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setEditingTerapia(null);
                    setDialogOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Aggiungi terapia
                </Button>
              </>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Somministrazioni di oggi
            </h4>
            {loadingTodaySlots && todaySlots.length === 0 ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : todaySlots.length === 0 ? (
              <p className="text-sm text-slate-500">Nessuna terapia in corso prevista oggi.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                <table className="min-w-[860px] w-full border-collapse text-left">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Orario</th>
                      <th className="px-3 py-2 font-semibold">Terapia</th>
                      <th className="px-3 py-2 font-semibold">Dettagli</th>
                      <th className="px-3 py-2 font-semibold">Stato</th>
                      <th className="px-3 py-2 font-semibold">Azione</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {todaySlots.map((slot) => (
                      <TodaySlotRow
                        key={slotKey(slot)}
                        slot={slot}
                        onSomministra={handleSomministra}
                        submitting={submittingId === slotKey(slot)}
                        note={noteBySlot[slotKey(slot)]}
                        onNoteChange={(s, val) =>
                          setNoteBySlot((prev) => ({ ...prev, [slotKey(s)]: val }))
                        }
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-slate-800 mb-3">Pianificazione terapie in corso</h4>
            {sortedTerapie.length === 0 ? (
            <p className="text-sm text-slate-500">Nessuna terapia in corso configurata.</p>
          ) : (
            <ul className="space-y-3">
              {sortedTerapie.map((te) => (
                <li key={te.id} className="rounded-lg border border-slate-200 p-3 bg-white">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Pill className="w-4 h-4 text-blue-500 shrink-0" />
                        <span className="font-semibold text-slate-900">{te.nome}</span>
                        <Badge variant={te.attiva ? "default" : "secondary"}>
                          {te.attiva ? "Attiva" : "Sospesa"}
                        </Badge>
                        {te.modificata_da_farmacia ? (
                          <Badge variant="outline" className="border-teal-200 bg-teal-50 text-teal-700">
                            Modificata da {te.farmacia_nome || "farmacia"}
                            {te.updated_at ? ` · ${new Date(te.updated_at).toLocaleDateString("it-IT")}` : ""}
                          </Badge>
                        ) : null}
                      </div>
                      {te.dosaggio ? (
                        <p className="text-sm text-slate-600 mt-1">{te.dosaggio}</p>
                      ) : null}
                      <p className="text-xs text-slate-500 mt-1">{summarizeTerapiaExtra(te)}</p>
                      {te.note ? (
                        <p className="text-xs text-slate-500 mt-1 italic">{te.note}</p>
                      ) : null}
                    </div>
                    {isAdmin ? (
                      <div className="flex flex-wrap gap-1.5 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingTerapia(te);
                            setDialogOpen(true);
                          }}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1" />
                          Modifica
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleToggleAttiva(te)}>
                          {te.attiva ? (
                            <>
                              <PauseCircle className="w-3.5 h-3.5 mr-1" />
                              Sospendi
                            </>
                          ) : (
                            <>
                              <PlayCircle className="w-3.5 h-3.5 mr-1" />
                              Riattiva
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(te)}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          Elimina
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
          </div>

          {isAdmin ? (
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-amber-600" />
                Storico cambi terapia
              </h4>
              {loadingStorico ? (
                <div className="flex justify-center py-3">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : storicoCambi.length === 0 ? (
                <p className="text-sm text-slate-500">
                  Nessun cambio terapia registrato. Usa <strong>Cambio terapia</strong> per allegare
                  il documento firmato dal medico o dall&apos;ASL.
                </p>
              ) : (
                <ul className="space-y-2">
                  {storicoCambi.map((d) => (
                    <li
                      key={d.id}
                      className="flex items-start justify-between gap-3 rounded-lg border border-amber-100 bg-amber-50/40 p-3"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{d.file_name}</p>
                        {d.cambio_dettagli ? (
                          <p className="text-xs text-slate-600 mt-1 italic">{d.cambio_dettagli}</p>
                        ) : null}
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-slate-500">
                          <span>
                            Firmato da:{" "}
                            <strong className="text-slate-700">
                              {d.ente_emittente === "asl"
                                ? "ASL"
                                : d.ente_emittente === "medico"
                                  ? "Medico"
                                  : d.origine || "—"}
                            </strong>
                          </span>
                          {(d.caricato_da_nome || d.createdByName) && (
                            <span>Caricato da: {d.caricato_da_nome || d.createdByName}</span>
                          )}
                          {d.createdAt ? (
                            <span>
                              {new Date(d.createdAt).toLocaleDateString("it-IT", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownloadCambio(d)}
                        title="Scarica"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {isAdmin ? (
        <TerapiaExtraDialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) setEditingTerapia(null);
          }}
          initialData={editingTerapia}
          onSubmit={handleCreateOrUpdate}
          saving={saving}
          dialogTitle={editingTerapia ? "Modifica terapia in corso" : "Nuova terapia in corso"}
          dialogDescription="Pianifica nome, dosaggio, giorni e orari della terapia."
          submitCreateLabel="Crea terapia"
          submitEditLabel="Salva modifiche"
        />
      ) : null}
      {isAdmin ? (
        <CambioTerapiaDialog
          open={cambioDialogOpen}
          onOpenChange={setCambioDialogOpen}
          fascicoloId={fascicoloId}
          ospiteLabel={`${ospiteInfo?.cognome || ""} ${ospiteInfo?.nome || ""}`.trim()}
          authHeaders={authHeaders}
          onSuccess={handleCambioSuccess}
        />
      ) : null}
    </>
  );
}
